import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { VIDEOJS_OPTIONS } from '../../config/const.config';

const Player = ({ videoUrl, height, width, autoplay, subtitles }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  let player = null;
  const playerOptions = VIDEOJS_OPTIONS;

  const convertSrtToVtt = async (srtUrl) => {
    try {
      const response = await fetch(srtUrl);
      const srtText = await response.text();

      const vttText = `WEBVTT\n\n${srtText
        .replace(/\r+/g, '')
        .replace(
          /(\d+)\n(\d{2}:\d{2}:\d{2}),(\d{3}) --> (\d{2}:\d{2}:\d{2}),(\d{3})/g,
          '$1\n$2.$3 --> $4.$5'
        )}`;

      return URL.createObjectURL(new Blob([vttText], { type: 'text/vtt' }));
    } catch (error) {
      return null;
    }
  };

  const addSubtitlesToPlayer = async () => {
    if (!playerRef.current || subtitles.length === 0) return;

    const existingTracks = playerRef.current.textTracks();

    if (existingTracks && existingTracks.length > 0) return;

    await Promise.all(
      subtitles.map(async (subtitle) => {
        let subtitleSrc = subtitle.subtitleUrl;

        if (subtitleSrc.endsWith('.srt')) {
          subtitleSrc = await convertSrtToVtt(subtitleSrc);
        }

        if (subtitleSrc) {
          playerRef.current.addRemoteTextTrack(
            {
              kind: 'subtitles',
              src: subtitleSrc,
              srclang: subtitle.languageCode,
              label: subtitle.languageLabel,
            },
            false
          );
        }
      })
    );
  };

  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = window.document.createElement('video-js');
      videoElement.id = 'video-player';
      videoElement.classList.add('vjs-default-skin');
      videoElement.classList.add('vjs-big-play-centered');
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';

      videoRef.current.appendChild(videoElement);

      playerRef.current = window.videojs(videoElement, {
        ...playerOptions,
        sources: [{ src: videoUrl }],
        autoplay,
      });

      player = playerRef.current;
    } else {
      player = playerRef.current;

      player.autoplay(autoplay);
      player.src(playerOptions.sources);
    }

    player.on('error', () => {
      const errorDisplay = player.getChild('errorDisplay');
      if (errorDisplay) errorDisplay.hide();
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [videoRef, videoUrl]);

  useEffect(() => {
    player = playerRef.current;

    if (videoUrl && subtitles.length > 0) {
      addSubtitlesToPlayer();
    }

    return () => {
      player.dispose();
      playerRef.current = null;
    };
  }, [playerRef, videoUrl]);

  return (
    <div className="data-vjs-player" style={{ width, height }}>
      <div ref={videoRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

Player.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  height: PropTypes.string.isRequired,
  width: PropTypes.string.isRequired,
  autoplay: PropTypes.bool.isRequired,
  subtitles: PropTypes.array.isRequired,
};

export default Player;
